import React, { useMemo, useRef, useState } from 'react';
import { Badge, Box, Checkbox, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useBoolean } from 'react-hanger';
import { EsPCAReportField } from 'c-sdk';
import {
    Add as AddIcon,
    KeyboardArrowDown,
    ListAlt,
    Remove as RemoveIcon,
} from '@mui/icons-material';
import { Button } from 'c-components';
import { Translate, useCommonTranslation } from 'c-translation';
import type { FieldSwitcherProps } from './types';
import { ReportView } from '../types';

const MAX_MULTI_FIELDS = 3;

const includedFieldsEs = [
    EsPCAReportField.DisplayTown,
    EsPCAReportField.DisplayTvRegion,
    EsPCAReportField.DisplayConurbation,
    EsPCAReportField.DisplayEnv,
    EsPCAReportField.DisplayFormat,
    EsPCAReportField.DisplayOwner,
    EsPCAReportField.FrameId,
    EsPCAReportField.DisplayLineItemName,
    EsPCAReportField.CreativeName,
    EsPCAReportField.Dow,
    EsPCAReportField.Hour,
    EsPCAReportField.DisplayScreenNameSummary,
    EsPCAReportField.StartDate,
];

const FieldSwitcher: React.FC<FieldSwitcherProps> = ({
    field,
    fields: externalFields,
    onFieldSwitch,
    onFieldsSwitch,
    view,
    onViewChange,
    isDsp = false,
    removeOverallView = false,
    showUnitId = false,
}) => {
    const btnRef = useRef();
    const openState = useBoolean(false);
    const t = useCommonTranslation();

    const [multiMode, setMultiMode] = useState(false);
    const [multiSelected, setMultiSelected] = useState<EsPCAReportField[]>([]);
    const isControlled = Array.isArray(externalFields) && typeof onFieldsSwitch === 'function';
    const [internalAppliedFields, setInternalAppliedFields] = useState<EsPCAReportField[]>([]);
    const appliedFields = isControlled ? externalFields ?? [] : internalAppliedFields;
    const setAppliedFields = (next: EsPCAReportField[]) => {
        if (!isControlled) setInternalAppliedFields(next);
    };
    const multiCount = appliedFields.length;

    const fieldsToUse = useMemo(
        () =>
            showUnitId
                ? includedFieldsEs.flatMap(f =>
                      f === EsPCAReportField.FrameId ? [f, EsPCAReportField.UnitId] : [f],
                  )
                : includedFieldsEs,
        [showUnitId],
    );

    const enterMulti = () => {
        const defaults =
            appliedFields.length > 0
                ? appliedFields
                : isDsp
                ? [EsPCAReportField.DisplayOwner, EsPCAReportField.DisplayFormat]
                : field
                ? [field as EsPCAReportField]
                : [];
        setMultiSelected(defaults);
        setMultiMode(true);
    };

    const exitMulti = () => {
        setMultiMode(false);
        setMultiSelected([]);
    };

    const handleExitMultiToSingle = () => {
        exitMulti();
        if (isControlled && multiCount > 0) onFieldsSwitch([]);
        else setAppliedFields([]);
    };

    const toggleMulti = (value: EsPCAReportField) => {
        setMultiSelected(prev => {
            if (prev.includes(value)) return prev.filter(v => v !== value);
            if (prev.length >= MAX_MULTI_FIELDS) return prev;
            return [...prev, value];
        });
    };

    const closeMenu = () => {
        openState.setFalse();
        exitMulti();
    };

    const openMenu = () => {
        if (!openState.value && multiCount > 1) {
            setMultiSelected(appliedFields);
            setMultiMode(true);
        }
        openState.toggle();
    };

    const applyMulti = () => {
        if (multiSelected.length === 0) return;
        if (multiSelected.length === 1) {
            onFieldSwitch(multiSelected[0]);
            if (!isControlled) setAppliedFields([]);
        } else if (onFieldsSwitch) {
            onFieldsSwitch(multiSelected);
            setAppliedFields(multiSelected);
        } else {
            onFieldSwitch(multiSelected[0]);
            setAppliedFields(multiSelected);
        }
        closeMenu();
    };

    const handleSingleSelect = (value: EsPCAReportField) => {
        onFieldSwitch(value);
        if (!isControlled) setAppliedFields([]);
        closeMenu();
    };

    return (
        <Box>
            <Badge
                badgeContent={multiCount > 1 ? multiCount : 0}
                color="error"
                overlap="rectangular"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Button
                    ref={btnRef}
                    onClick={openMenu}
                    color={view === ReportView.Field ? 'primary' : 'secondary'}
                    className="pca-group-by"
                >
                    <ListAlt sx={{ mr: 0.5 }} />
                    {t('Modules.Main.Campaigns.Overview.Reports.byFieldButton')}
                    <KeyboardArrowDown />
                </Button>
            </Badge>

            <Menu anchorEl={btnRef.current} open={openState.value} onClose={closeMenu}>
                {multiMode ? (
                    <Box>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ px: 2, py: 1 }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {`Group by (${multiSelected.length}/${MAX_MULTI_FIELDS})`}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={handleExitMultiToSingle}
                                aria-label="Exit multi-select"
                            >
                                <RemoveIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                        {fieldsToUse.map(value => {
                            const checked = multiSelected.includes(value);
                            const atMax = multiSelected.length >= MAX_MULTI_FIELDS;
                            const disabled = !checked && atMax;
                            return (
                                <MenuItem
                                    key={`multi-${value}`}
                                    onClick={() => !disabled && toggleMulti(value)}
                                    disabled={disabled}
                                    sx={{ fontSize: '0.95rem', pl: 1 }}
                                >
                                    <Checkbox
                                        checked={checked}
                                        size="small"
                                        sx={{ mr: 0.5, p: 0.5 }}
                                        disableRipple
                                    />
                                    {t(
                                        `Modules.Main.Campaigns.Overview.Reports.fields.${value}` as any,
                                    )}
                                </MenuItem>
                            );
                        })}
                        <Box sx={{ px: 1.5, py: 1 }}>
                            <Button
                                fullWidth
                                onClick={applyMulti}
                                disabled={multiSelected.length === 0}
                            >
                                Apply
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box>
                        <MenuItem
                            onClick={enterMulti}
                            sx={{ fontSize: '0.95rem', fontWeight: 600 }}
                        >
                            <AddIcon fontSize="small" sx={{ mr: 1 }} />
                            Multi grouping
                        </MenuItem>

                        {!removeOverallView && (
                            <MenuItem
                                selected={view === ReportView.Overall}
                                onClick={() => {
                                    onViewChange(ReportView.Overall);
                                    closeMenu();
                                }}
                                sx={{ fontSize: '0.95rem' }}
                            >
                                <Translate path="Modules.Main.Campaigns.Overview.Reports.overallTabTitle" />
                            </MenuItem>
                        )}

                        {fieldsToUse.map(value => (
                            <MenuItem
                                key={`single-${value}`}
                                selected={field === value && view === ReportView.Field}
                                onClick={() => handleSingleSelect(value)}
                                className={`group-by-field-${field}`}
                                sx={{ fontSize: '0.95rem' }}
                            >
                                {t(
                                    `Modules.Main.Campaigns.Overview.Reports.fields.${value}` as any,
                                )}
                            </MenuItem>
                        ))}
                    </Box>
                )}
            </Menu>
        </Box>
    );
};

export default FieldSwitcher;
